import { Link } from 'react-router-dom';
import { humanizeEnum } from '../../lib/fieldLabels.js';

// Shared governed-run chrome. Extracted verbatim from the AI Orchestration
// Hub's inline `GovernedRunMeta` (AiOrchestrationHubPage.jsx) so every governed
// AI surface — orchestration hub, Director's daily brief, placemat draft — can
// render an identical run dossier: run ID, agent, scope, generated-at,
// confidence, evidence count, review-state / status / guardrail chips, and the
// "Open governed run" deep link. Keep the DOM output identical to the original
// so the hub's tests stay green.

export function formatRunTimestamp(value) {
  if (!value) return 'Pending';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function formatCitationCount(count = 0) {
  const total = Number(count || 0);
  return `${total} citation${total === 1 ? '' : 's'}`;
}

export function GovernedRunChrome({ result }) {
  if (!result?.runId) return null;
  return (
    <div className="ai-governance-block" data-testid={`ai-governance-${result.runId}`}>
      <div className="ai-governance-grid">
        <div>
          <span className="eyebrow">AI run ID</span>
          <strong>{result.runId}</strong>
        </div>
        <div>
          <span className="eyebrow">Agent</span>
          <strong>{result.agentId || 'Unknown agent'}</strong>
        </div>
        <div>
          <span className="eyebrow">Scope</span>
          <strong>
            {result.scopeType || 'scope'}
            {result.scopeId ? ` · ${result.scopeId}` : ''}
          </strong>
        </div>
        <div>
          <span className="eyebrow">Generated</span>
          <strong>{formatRunTimestamp(result.generatedAt)}</strong>
        </div>
        <div>
          <span className="eyebrow">Confidence</span>
          <strong>
            {result.confidence != null ? `${Math.round(Number(result.confidence) * 100)}%` : 'N/A'}
          </strong>
        </div>
        <div>
          <span className="eyebrow">Evidence</span>
          <strong>{formatCitationCount(result.citations?.length || 0)}</strong>
        </div>
      </div>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <span className="chip outline sm">AI run ID: {result.runId}</span>
        <span className="chip outline sm" title={result.reviewState || 'pending_human_review'}>
          {humanizeEnum(result.reviewState || 'pending_human_review')}
        </span>
        <span className="chip outline sm" title={result.status || 'completed'}>
          {humanizeEnum(result.status || 'completed')}
        </span>
        <span className="chip outline sm">
          Guardrail {String(result.guardrailOutcome || 'pending').replace(/_/g, ' ')}
        </span>
        <Link className="btn ghost sm" to={`/ai/runs/${result.runId}`}>
          Open governed run
        </Link>
      </div>
    </div>
  );
}
